import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { ResourceIcon } from './ResourceIcon';
import type { ResourceKey } from '../../lib/game';

export interface SlotReelsProps {
  /** The universe of symbols a reel can show while cycling. */
  symbols: readonly ResourceKey[];
  /** The deterministic outcome — known before the spin starts. */
  result: readonly [ResourceKey, ResourceKey, ResourceKey];
  /** Flips true to start a new spin. The component owns the reveal timing. */
  spinning: boolean;
  /** Fires once the third reel has settled on `result`. */
  onSettled?: () => void;
  size?: number;
}

/** Reel stop times, staggered so the machine reads left-to-right. */
const STOP_AT = [900, 1450, 2100] as const;
const CYCLE_MS = 70;

/**
 * Three independently-spinning reels.
 *
 * The true result is already known the instant the player clicks — the
 * engine is synchronous and deterministic — so "spinning" here is theatre,
 * not suspense about the outcome. Each reel free-runs through random symbols
 * on its own interval and is told to stop and lock onto its real result at a
 * staggered time, which is what actually reads as a slot machine rather than
 * three numbers appearing at once.
 */
export function SlotReels({ symbols, result, spinning, onSettled, size = 36 }: SlotReelsProps) {
  const [displayed, setDisplayed] = useState<[ResourceKey, ResourceKey, ResourceKey]>([
    result[0],
    result[1],
    result[2],
  ]);
  const [settled, setSettled] = useState<[boolean, boolean, boolean]>([true, true, true]);
  const [bounce, setBounce] = useState<[number, number, number]>([0, 0, 0]);
  const spinToken = useRef(0);

  useEffect(() => {
    if (!spinning) return;
    const token = ++spinToken.current;
    setSettled([false, false, false]);

    const intervals = [0, 1, 2].map((i) =>
      window.setInterval(() => {
        setDisplayed((prev) => {
          const next = [...prev] as [ResourceKey, ResourceKey, ResourceKey];
          next[i] = symbols[Math.floor(Math.random() * symbols.length)]!;
          return next;
        });
      }, CYCLE_MS),
    );

    const timeouts = STOP_AT.map((delay, i) =>
      window.setTimeout(() => {
        if (spinToken.current !== token) return;
        window.clearInterval(intervals[i]);
        setDisplayed((prev) => {
          const next = [...prev] as [ResourceKey, ResourceKey, ResourceKey];
          next[i] = result[i]!;
          return next;
        });
        setSettled((prev) => {
          const next = [...prev] as [boolean, boolean, boolean];
          next[i] = true;
          return next;
        });
        setBounce((prev) => {
          const next = [...prev] as [number, number, number];
          next[i] = next[i]! + 1;
          return next;
        });
        if (i === STOP_AT.length - 1) onSettled?.();
      }, delay),
    );

    return () => {
      intervals.forEach(window.clearInterval);
      timeouts.forEach(window.clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning]);

  return (
    <div className="flex gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={`${i}-${bounce[i]}`}
          className={cn(
            'flex items-center justify-center rounded-panel border shadow-inset-edge',
            settled[i] ? 'sg-reel-settle border-edge-strong bg-surface-base' : 'border-edge bg-surface-base',
            !settled[i] && 'sg-reel-spinning',
          )}
          style={{ height: size * 2.2, width: size * 2.2 }}
        >
          <ResourceIcon resource={displayed[i]!} size={size} glow={settled[i]} />
        </div>
      ))}
    </div>
  );
}
